// txSigner.js — payer-routing layer for on-chain writes (the 3-scenario model).
//
//   FREE tier       → the user's own NCOG wallet signs, confirms, and pays gas
//                     (window.ncogProvider → ncog_sendTransaction). No key in the client.
//   SPONSORED tier  → organization (subscription) / Securus users: the server relayer signs + funds
//                     the write via the backend (POST /mail/chain/*). No key in the client.
//
// This replaces the single hardcoded relayer key that used to sign EVERY write in the browser bundle.
// chain-helper.js delegates transactionAction() here; the tier decides the payer.
import { ethers } from 'ethers';
import web3Config from '../web3/web3Config.json';
import db from '../db/db-service.js';
import { fetchAPI } from '../service/api-actions.js';

export const Tier = { FREE: 'free', SPONSORED: 'sponsored' };

/**
 * Resolve the caller's payer tier. The organization/subscription/Securus signal in this app is the
 * presence of organizationData in IndexedDB — the same branch the original (commented-out)
 * transactionAction keyed on. No org data → free tier (the user's own wallet pays + signs).
 *
 * NOTE: this is the client-side hint used to pick the signing path. The backend re-checks tier
 * server-side (ISponsorshipPolicy) before it ever sponsors a write — the client can't self-promote.
 *
 * The organizationData PAYLOAD is sealed under the dMail DEK (db/at-rest.js), but `uuid` /
 * `organizationId` are deliberately left as cleartext columns precisely so this stays a raw, synchronous
 * table read with no DEK dependency: they are opaque server-issued identifiers carrying no user content,
 * and routing gas payment must not break because a key could not be unwrapped. Read them here ONLY as a
 * presence check — anything inside the sealed payload must go through at-rest.readOrganizationData().
 */
export const resolveTier = async () => {
  try {
    const org = await db.table('organizationData').toArray();
    if (org?.length > 0 && (org[0]?.uuid || org[0]?.organizationId)) return Tier.SPONSORED;
  } catch (_) {
    // Free users have no organizationData table/rows — fall through to FREE.
  }
  return Tier.FREE;
};

/**
 * functionName → server-sponsored endpoint. Grows as backend endpoints are migrated off the relayer
 * (strangler-fig migration). buildBody maps the positional functionParams (ABI order) to JSON.
 */
export const SPONSORED_OPS = {
  // helper.createFolder(_username, _name, _date, _folderProperties[{key,value}])
  createFolder: {
    path: 'mail/chain/folder',
    buildBody: (p) => ({ username: p?.[0] ?? '', name: p?.[1] ?? '', date: p?.[2] ?? '', folderProperties: p?.[3] ?? [] }),
  },
  // helper.deleteFolder(_user, _folderId)  — folderId → string (uint256; avoids BigInt JSON issues)
  deleteFolder: {
    path: 'mail/chain/folder/delete',
    buildBody: (p) => ({ user: p?.[0] ?? '', folderId: String(p?.[1] ?? 0) }),
  },
  // helper.updateSettings(_username, _key, _options[{key,value}])
  updateSettings: {
    path: 'mail/chain/settings',
    buildBody: (p) => ({ username: p?.[0] ?? '', key: p?.[1] ?? '', options: p?.[2] ?? [] }),
  },
  // host.storeSendMail(_recipient[], _encryptedData[], _emailDetails[], _subject, _attributes[{key,value}])
  storeSendMail: {
    path: 'mail/chain/mail',
    buildBody: (p) => ({
      recipient: p?.[0] ?? [], encryptedData: p?.[1] ?? [], emailDetails: p?.[2] ?? [],
      subject: p?.[3] ?? '', attributes: p?.[4] ?? [],
    }),
  },
  // host.addEmails(_to[], _cc[], _bcc[], _toEncryptedData[], _ccEncryptedData[], _bccEncryptedData[], _senderName, _emailDetails[], _subject, _attributes[{key,value}])
  addEmails: {
    path: 'mail/chain/emails',
    buildBody: (p) => ({
      to: p?.[0] ?? [], cc: p?.[1] ?? [], bcc: p?.[2] ?? [],
      toEncryptedData: p?.[3] ?? [], ccEncryptedData: p?.[4] ?? [], bccEncryptedData: p?.[5] ?? [],
      senderName: p?.[6] ?? '', emailDetails: p?.[7] ?? [], subject: p?.[8] ?? '', attributes: p?.[9] ?? [],
    }),
  },
  // helper.saveQuickSend(userName, quickAccessUserEmail, quickAccessUserName)
  saveQuickSend: {
    path: 'mail/chain/quicksend',
    buildBody: (p) => ({ userName: p?.[0] ?? '', quickAccessUserEmail: p?.[1] ?? '', quickAccessUserName: p?.[2] ?? '' }),
  },
  // helper.deleteQuickSendEntry(userName, index)
  deleteQuickSendEntry: {
    path: 'mail/chain/quicksend/delete',
    buildBody: (p) => ({ userName: p?.[0] ?? '', index: String(p?.[1] ?? 0) }),
  },
  // helper.addWeb2Emails(username, emails[])
  addWeb2Emails: {
    path: 'mail/chain/web2emails',
    buildBody: (p) => ({ username: p?.[0] ?? '', emails: p?.[1] ?? [] }),
  },
  // helper.setAttribute(_user, _id[], _attType, _key, _value)  — _id → string[] (uint256[])
  setAttribute: {
    path: 'mail/chain/attribute',
    buildBody: (p) => ({
      user: p?.[0] ?? '', id: (p?.[1] ?? []).map((x) => String(x)),
      attType: p?.[2] ?? '', key: p?.[3] ?? '', value: !!p?.[4],
    }),
  },
  // host.createAndUpdateSettings(username, encrytedData)  — ABI misspells "encrytedData"; keep it
  createAndUpdateSettings: {
    path: 'mail/chain/settings/create',
    buildBody: (p) => ({ username: p?.[0] ?? '', encrytedData: p?.[1] ?? '' }),
  },
  // host.updateFolder(_user, _folderId, _name, _date, _filterType, _filterValue, _emails[])
  updateFolder: {
    path: 'mail/chain/folder/update',
    buildBody: (p) => ({
      user: p?.[0] ?? '', folderId: String(p?.[1] ?? 0), name: p?.[2] ?? '', date: p?.[3] ?? '',
      filterType: p?.[4] ?? '', filterValue: p?.[5] ?? '', emails: p?.[6] ?? [],
    }),
  },
  // host.permanentlyDeleteEmail(recipient, emailId[])  — emailId → string[] (uint256[])
  permanentlyDeleteEmail: {
    path: 'mail/chain/email/delete',
    buildBody: (p) => ({ recipient: p?.[0] ?? '', emailId: (p?.[1] ?? []).map((x) => String(x)) }),
  },
  // host.updateEmailAttributes(username, emailId[], _attributes[{key,value}])
  updateEmailAttributes: {
    path: 'mail/chain/email/attributes',
    buildBody: (p) => ({ username: p?.[0] ?? '', emailId: (p?.[1] ?? []).map((x) => String(x)), attributes: p?.[2] ?? [] }),
  },
  // calendar.addEvent(mergedEvent[{uuid,uid,title,fromTime,toTime}], username, inputContacts{contact:[{key,value}]})
  addEvent: {
    path: 'mail/chain/event',
    buildBody: (p) => ({ mergedEvent: p?.[0] ?? [], username: p?.[1] ?? '', inputContacts: p?.[2] ?? { contact: [] } }),
  },
  // calendar.editEvent(eventIndex[], params{uuid,uid,title,fromTime,toTime}, username)
  editEvent: {
    path: 'mail/chain/event/edit',
    buildBody: (p) => ({ eventIndex: p?.[0] ?? [], params: p?.[1] ?? {}, username: p?.[2] ?? '' }),
  },
  // calendar.deleteEvent(eventIndex, username)
  deleteEvent: {
    path: 'mail/chain/event/delete',
    buildBody: (p) => ({ eventIndex: p?.[0] ?? '', username: p?.[1] ?? '' }),
  },
};

/**
 * FREE tier: the user's NCOG wallet signs + broadcasts + pays gas. We ABI-encode the call locally
 * (ethers) and hand the wallet a plain {from,to,data} tx; the extension prompts the user to confirm.
 * Returns the tx hash.
 */
export const signWithUserWallet = async ({ contractId, contractABI, functionName, functionParams, from }) => {
  const provider = typeof window !== 'undefined' ? window.ncogProvider : undefined;
  if (!provider?.request) {
    throw new Error('NCOG wallet not found — install or enable the NCOG wallet to sign transactions.');
  }

  let sender = from;
  if (!sender) {
    const accts = await provider.request({ method: 'ncog_accounts' }).catch(() => null);
    sender = accts?.selectedAccount?.accountAddress || (Array.isArray(accts) ? accts[0] : undefined);
  }

  const data = new ethers.Interface(contractABI).encodeFunctionData(functionName, functionParams);

  // Gas, gasPrice and nonce are deliberately left to the wallet: it alone knows which
  // key signs (its signing address is not the account address the dapp sees here), and
  // the nonce and gas estimate must be derived from THAT address.
  const tx = { from: sender, to: contractId, data, value: '0x0' };

  // The wallet has historically been able to die without answering (background-script
  // crash), which would hang this await forever. Bound it so callers always regain
  // control and can report a failure rather than spinning.
  const raw = await Promise.race([
    provider.request({ method: 'ncog_sendTransaction', params: [tx] }),
    new Promise((_, reject) => setTimeout(
      () => reject(new Error('The wallet did not respond. Please reopen it and try again.')), 60000)),
  ]);

  // Wallet contract: { txHash: "0x..." } on success, { error: "..." } on failure —
  // possibly wrapped in a { response } envelope by the extension's messaging layer.
  const inner = raw?.response ?? raw;
  if (typeof inner === 'string') return inner;
  if (inner?.error) throw new Error(String(inner.error));
  const h = inner?.txHash || inner?.hash || inner?.transactionHash;
  if (typeof h === 'string' && h.length) return h;
  // No hash and no error: the transaction may or may not have been broadcast. Treat as
  // unconfirmed — callers that care must verify against the chain.
  console.warn('[txSigner] wallet returned no hash and no error:', JSON.stringify(raw));
  return null;
};

/**
 * SPONSORED tier: POST to the backend, which signs + funds with the server relayer. Only ops present
 * in SPONSORED_OPS have an endpoint; anything else returns { migrated: false } so the caller can decide.
 */
export const submitSponsored = async ({ functionName, functionParams }) => {
  const op = SPONSORED_OPS[functionName];
  if (!op) return { migrated: false, txHash: null };
  const res = await fetchAPI(op.path, 'POST', op.buildBody(functionParams));
  return { migrated: true, txHash: res?.txHash || null };
};
